import axios from "axios";
import { NavigateFunction } from "react-router-dom";
import { baseURL } from "../globals/constants";
import { showErrorToast } from "../utils/Toast";
import i18next from "i18next";

export type CronJobRunStatus = "running" | "success" | "error";

export interface CronJob {
  id: string;
  name: string;
  jobType: string;
  schedule: string;
  timezone: string;
  enabled: boolean;
  description?: string;
  defaultKey?: string;
  lastRunAt?: string;
  lastFinishedAt?: string;
  lastDurationMs?: number;
  lastStatus?: CronJobRunStatus;
  lastError?: string;
}

export interface CronJobType {
  key: string;
  label: string;
  description: string;
  defaultSchedule: string;
}

export type CronJobValues = Pick<CronJob, "name" | "jobType" | "schedule" | "timezone" | "enabled" | "description">;

const authHeaders = () => ({
  headers: {
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  },
});

const handleError = (error: any, isMobile: boolean, navigate: NavigateFunction, fallbackMessage: string) => {
  console.log("error ", error);
  if (error?.response?.status === 401) {
    showErrorToast(i18next.t("messages.sessionExpired"), isMobile);
    localStorage.removeItem("access_token");
    navigate("/");
  } else {
    showErrorToast(error?.response?.data?.message || fallbackMessage, isMobile);
  }
};

export const getCronJobs = async (isMobile: boolean, navigate: NavigateFunction): Promise<CronJob[]> => {
  try {
    const response = await axios.get(`${baseURL}/v1/cron-jobs`, authHeaders());
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load cron jobs");
    return [];
  }
};

export const getCronJobTypes = async (isMobile: boolean, navigate: NavigateFunction): Promise<CronJobType[]> => {
  try {
    const response = await axios.get(`${baseURL}/v1/cron-jobs/types`, authHeaders());
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load job types");
    return [];
  }
};

export const createCronJob = async (values: CronJobValues, isMobile: boolean, navigate: NavigateFunction): Promise<boolean> => {
  try {
    await axios.post(`${baseURL}/v1/cron-jobs`, values, authHeaders());
    return true;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not create cron job");
    return false;
  }
};

export const updateCronJob = async (
  id: string,
  values: Partial<CronJobValues>,
  isMobile: boolean,
  navigate: NavigateFunction
): Promise<boolean> => {
  try {
    await axios.patch(`${baseURL}/v1/cron-jobs/${id}`, values, authHeaders());
    return true;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not update cron job");
    return false;
  }
};

export const deleteCronJob = async (id: string, isMobile: boolean, navigate: NavigateFunction): Promise<boolean> => {
  try {
    await axios.delete(`${baseURL}/v1/cron-jobs/${id}`, authHeaders());
    return true;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not delete cron job");
    return false;
  }
};

export const runCronJob = async (id: string, isMobile: boolean, navigate: NavigateFunction): Promise<boolean> => {
  try {
    await axios.post(`${baseURL}/v1/cron-jobs/${id}/run`, {}, authHeaders());
    return true;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not start cron job");
    return false;
  }
};
